import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { ArrowLeft, Files, Loader2 } from 'lucide-react';
import {
  creativeStrategyApi,
  type StrategyCorpusDetail,
  type StrategyCorpusFile,
  type StrategyCorpusGap,
} from '@/services/api';

const KIND_LABEL: Record<string, string> = {
  category_ad_corpus_crawl: 'Ad crawl',
  category_corpus_walk: 'Review walk',
  pack: 'Pack',
  unreadable: 'Unreadable',
};

function kindLabel(kind: string): string {
  return KIND_LABEL[kind] || kind;
}

export function CorpusPage() {
  const { docketId, sourceId } = useParams();
  if (docketId && sourceId) {
    return <CorpusDetail docketId={docketId} sourceId={sourceId} />;
  }
  return <CorpusIndex />;
}

function CorpusIndex() {
  const [files, setFiles] = useState<StrategyCorpusFile[]>([]);
  const [gaps, setGaps] = useState<StrategyCorpusGap[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    creativeStrategyApi.listCorpus()
      .then((res) => {
        setFiles(res.files);
        setGaps(res.awaiting_corpus);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-navy-900 flex items-center justify-center text-white">
            <Files size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-navy-900">Category corpus</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Ad Library crawls and Amazon review walks published for each library. A rebuild merges each source with the clients' own packs once it carries at least 20 concepts or 10 templates; thinner sources are listed in the run, not merged.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>
        )}
        {loading ? (
          <div className="flex items-center text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Listing corpus…
          </div>
        ) : error ? null : files.length === 0 ? (
          <div className="mb-6 rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
            No corpus files yet. Garden runs <span className="font-mono text-xs">category_ad_corpus_crawl</span> and <span className="font-mono text-xs">category_corpus_walk</span> publish JSON under <span className="font-mono text-xs">creative_strategy/corpus/&lt;docket_id&gt;/</span>.
          </div>
        ) : (
          <div className="mb-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Source</th>
                  <th className="text-left px-4 py-2.5 font-medium">Library</th>
                  <th className="text-left px-4 py-2.5 font-medium">Kind</th>
                  <th className="text-right px-4 py-2.5 font-medium">Concepts</th>
                  <th className="text-right px-4 py-2.5 font-medium">Strategies</th>
                  <th className="text-right px-4 py-2.5 font-medium">Templates</th>
                  <th className="text-left px-4 py-2.5 font-medium">Refresh</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {files.map((f) => (
                  <tr key={`${f.docket_id}/${f.source_id}`} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/knowledge/creative-strategy/corpus/${encodeURIComponent(f.docket_id)}/${encodeURIComponent(f.source_id)}`}
                        className="font-medium text-navy-900 hover:text-brand-teal"
                      >
                        {f.filename}
                      </Link>
                      <div className="text-xs text-slate-400 font-mono">{f.run_id || f.amazon_path || f.source_id}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <Link to={`/knowledge/creative-strategy/library/${encodeURIComponent(f.docket_id)}`} className="text-slate-700 hover:text-brand-teal">
                        {f.docket_name}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{kindLabel(f.kind)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{f.concepts}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{f.strategies}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{f.templates}</td>
                    <td className="px-4 py-2.5">
                      <span className={clsx(
                        'text-[11px] font-medium rounded-full px-2 py-0.5 border',
                        f.used_by_refresh
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-amber-50 text-amber-700 border-amber-200',
                      )}>
                        {f.used_by_refresh ? 'read' : 'ignored'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {gaps.length > 0 && (
          <>
            <h2 className="text-sm font-semibold text-navy-900 mb-2">Waiting on a crawl or walk</h2>
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium">Library</th>
                    <th className="text-left px-4 py-2.5 font-medium">Amazon paths</th>
                    <th className="text-left px-4 py-2.5 font-medium">Competitors</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {gaps.map((g) => (
                    <tr key={g.docket_id}>
                      <td className="px-4 py-2.5">
                        <Link to={`/knowledge/creative-strategy/library/${encodeURIComponent(g.docket_id)}`} className="font-medium text-navy-900 hover:text-brand-teal">
                          {g.docket_name}
                        </Link>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600">{g.amazon_paths.join(' · ') || '—'}</td>
                      <td className="px-4 py-2.5 text-slate-600">{g.competitor_set.join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CorpusDetail({ docketId, sourceId }: { docketId: string; sourceId: string }) {
  const [detail, setDetail] = useState<StrategyCorpusDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    creativeStrategyApi.getCorpusFile(docketId, sourceId)
      .then(setDetail)
      .catch((err: Error) => setError(err.message));
  }, [docketId, sourceId]);

  if (!detail) {
    return (
      <div className="p-6 text-sm text-slate-500">
        {error ? <span className="text-red-700">{error}</span> : <><Loader2 size={14} className="inline animate-spin mr-2" />Loading corpus…</>}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="px-6 py-4 border-b border-slate-200 bg-white">
        <Link to="/knowledge/creative-strategy/corpus" className="text-xs text-slate-500 hover:text-navy-900 inline-flex items-center gap-1 mb-2">
          <ArrowLeft size={12} /> Corpus
        </Link>
        <h1 className="text-xl font-semibold text-navy-900">{detail.filename}</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          {detail.docket_name} · {kindLabel(detail.kind)}
          {detail.run_id ? ` · ${detail.run_id}` : ''}
          {detail.used_by_refresh ? '' : ' · ignored by refresh'}
        </p>
        <p className="text-xs text-slate-400 font-mono mt-1">{detail.path}</p>
      </div>
      <div className="flex-1 overflow-auto p-6 space-y-6">
        <CorpusList title="Buyer concepts" rows={detail.buyer_concepts.map((c) => ({
          name: c.name,
          meta: [c.concept_type, c.support != null ? String(c.support) : ''].filter(Boolean).join(' · '),
          body: c.evidence,
        }))} />
        <CorpusList title="Strategies" rows={detail.strategy_rows.map((s) => ({
          name: s.name || 'Untitled',
          meta: s.strategy_type || '',
          body: s.argument,
        }))} />
        <CorpusList title="Templates" rows={detail.template_rows.map((t) => ({
          name: t.name,
          meta: [t.layout_archetype, t.ads != null ? `${t.ads} ads` : ''].filter(Boolean).join(' · '),
          body: t.structure,
        }))} />
      </div>
    </div>
  );
}

function CorpusList({ title, rows }: { title: string; rows: Array<{ name: string; meta?: string; body?: string }> }) {
  return (
    <section>
      <h2 className="text-sm font-semibold text-navy-900 mb-2">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">None in this file.</p>
      ) : (
        <ul className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
          {rows.map((row) => (
            <li key={row.name} className="px-4 py-2.5">
              <div className="font-medium text-navy-900">{row.name}</div>
              {row.meta && <div className="text-xs text-slate-500">{row.meta}</div>}
              {row.body && <div className="text-sm text-slate-600 mt-0.5">{row.body}</div>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
